"use client";

import { useTranslations } from "next-intl";
import { FC } from "react";

import { useCommentsFeed } from "@/app/(main)/components/comments_feed_provider";
import { openKeyFactorsSectionAndScrollTo } from "@/app/(main)/questions/[id]/components/key_factors/utils";
import { PostStatus, PostWithForecasts } from "@/types/post";
import { sendAnalyticsEvent } from "@/utils/analytics";
import { getQuestionForecastAvailability } from "@/utils/questions/forecastAvailability";
import { isQuestionPost } from "@/utils/questions/helpers";

import {
  MAX_TOP_KEY_FACTORS,
  useTopKeyFactorsCarouselItems,
} from "./hooks/use_top_key_factors_carousel_items";
import KeyFactorDetailOverlay from "./key_factor_detail_overlay";
import KeyFactorsCarousel from "./key_factors_carousel";
import KeyFactorsConsumerCarousel from "./key_factors_consumer_carousel";
import { useQuestionLayout } from "../question_layout/question_layout_context";

type Props = {
  post: PostWithForecasts;
};

const KeyFactorsQuestionConsumerSection: FC<Props> = ({ post }) => {
  const { combinedKeyFactors: keyFactors } = useCommentsFeed();
  const t = useTranslations();
  const {
    requestKeyFactorsExpand,
    keyFactorOverlay,
    openKeyFactorOverlay,
    closeKeyFactorOverlay,
  } = useQuestionLayout();

  const { items: topItems, totalCount } = useTopKeyFactorsCarouselItems({
    keyFactors,
    limit: MAX_TOP_KEY_FACTORS,
  });

  if (post.status === PostStatus.RESOLVED) return null;

  const postForecastAvailability = isQuestionPost(post)
    ? getQuestionForecastAvailability(post.question)
    : null;
  const forecastIsEmpty =
    !!postForecastAvailability?.isEmpty &&
    !postForecastAvailability?.cpRevealsOn;

  if (topItems.length === 0 && !forecastIsEmpty) return null;

  const openKeyFactorsElement = (selector: string) => {
    requestKeyFactorsExpand?.();
    openKeyFactorsSectionAndScrollTo({ selector, mobileOnly: false });
    sendAnalyticsEvent("KeyFactorClick", { event_label: "fromTopList" });
  };

  return (
    <div
      className="-ml-4 flex w-[calc(100%+32px)] flex-col pb-4 sm:ml-0 sm:mt-8 sm:w-full"
      id="top-key-factors"
    >
      <div className="mb-4 flex items-center justify-between px-4 sm:px-0">
        <div className="text-sm text-blue-800 dark:text-blue-800-dark">
          {t("topKeyFactors")}
        </div>
        {!forecastIsEmpty && (
          <button
            onClick={() => {
              openKeyFactorsElement("[id='key-factors']");
              sendAnalyticsEvent("KeyFactorViewAllClick");
            }}
            className="text-center text-sm font-normal leading-5 text-blue-600 hover:text-blue-700 dark:text-blue-600-dark dark:hover:text-blue-700-dark"
          >
            {t("viewAll", { count: totalCount })}
          </button>
        )}
      </div>

      {forecastIsEmpty ? (
        <KeyFactorsCarousel
          listClassName="pb-0 [&>:first-child]:pl-4 [&>:last-child]:pr-4 sm:[&>:first-child]:pl-0 sm:[&>:last-child]:pr-0"
          items={Array.from({ length: 5 })}
          renderItem={(_, i) => (
            <div
              key={i}
              className="h-[196px] w-[160px] shrink-0 rounded-xl bg-blue-200 dark:bg-blue-200-dark sm:w-[200px]"
            />
          )}
        />
      ) : (
        <KeyFactorsConsumerCarousel post={post} items={topItems} />
      )}

      {keyFactorOverlay?.kind === "keyFactor" && (
        <KeyFactorDetailOverlay
          keyFactor={keyFactorOverlay.keyFactor}
          allKeyFactors={keyFactors}
          post={post}
          onClose={closeKeyFactorOverlay}
          onSelectKeyFactor={(kf) => openKeyFactorOverlay(kf)}
        />
      )}
      {keyFactorOverlay?.kind === "questionLink" && (
        <KeyFactorDetailOverlay
          key={keyFactorOverlay.link.id}
          questionLink={keyFactorOverlay.link}
          post={post}
          onClose={closeKeyFactorOverlay}
        />
      )}
    </div>
  );
};

export default KeyFactorsQuestionConsumerSection;
